/* Envuelto en una IIFE con guarda, mismo patron que dd0-quote-slider.js. Este fichero lo carga
   mas de una seccion (y main-product lo emite dentro del bucle de bloques), asi que en una misma
   pagina el navegador puede ejecutarlo DOS veces. Con las declaraciones en el ambito global la
   segunda ejecucion muere entera con "Identifier ... has already been declared" antes de correr
   una linea. El cuerpo se deja con su sangria original a proposito: el diff es el envoltorio.
   Comprobacion: node tools/check_scripts_twice.js */
(() => {
  if (customElements.get('dd0-ugc-video')) return;

/**
 * DD0 UGC Videos — web components
 *
 * <dd0-ugc-video data-mode="facade|autoplay">
 *   Wraps a <video> + poster + play button. Lazy-loads <source data-src>
 *   on click (facade) or on IntersectionObserver enter (autoplay muted).
 *   Pauses on exit. Cleans observer on disconnect.
 *
 * <dd0-ugc-carousel>
 *   Native scroll-snap viewport + prev/next arrows. ~25 lines, no slider lib.
 */

class DD0UGCVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.poster = this.querySelector('.dd0-ugc-video__poster');
    this.playBtn = this.querySelector('.dd0-ugc-video__play');
    this.soundBtn = this.querySelector('.dd0-ugc-video__sound');
    this.mode = this.dataset.mode || 'facade';
    this.nativeControls = this.dataset.nativeControls === 'true';
    this.loaded = false;

    if (this.playBtn) {
      this.playBtn.addEventListener('click', this.onPlayClick.bind(this));
    }
    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onSoundClick();
      });
    }

    if (!this.nativeControls) {
      this.addEventListener('click', (e) => {
        // Skip if click landed on an interactive child (play btn, sound btn, link, dot)
        if (e.target.closest('button, a, [data-dot-index]')) return;
        this.onVideoTap();
      });
    }

    if (this.mode === 'autoplay') {
      // Medio video visible para arrancar (tarjetas UGC), salvo data-threshold: un video mas alto que dos pantallas
      // nunca llega al 50 % y no arrancaria. Lo pasa el video de "Imagen con texto" (0.01).
      this.io = new IntersectionObserver(this.onIntersect.bind(this), { threshold: parseFloat(this.dataset.threshold) || 0.5 });
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  onPlayClick() {
    this.activate({ autoplay: false, unmute: true });
  }

  onVideoTap() {
    if (!this.video) return;
    if (!this.loaded) {
      // First tap loads sources and plays with sound (same as play button)
      this.activate({ autoplay: false, unmute: true });
      return;
    }
    if (this.video.paused) {
      this.video.play().catch(() => {});
      this.classList.add('is-playing');
    } else {
      this.video.pause();
      this.classList.remove('is-playing');
    }
  }

  onSoundClick() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.updateSoundIcon();
  }

  onIntersect(entries) {
    if (!this.video) return;
    const visible = entries[0].isIntersecting;
    if (visible) {
      this.activate({ autoplay: true, unmute: false });
    } else if (this.loaded) {
      this.video.pause();
    }
  }

  activate({ autoplay, unmute }) {
    if (!this.video) return;
    if (!this.loaded) {
      this.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      this.video.load();
      this.loaded = true;
    }
    if (unmute) {
      this.video.muted = false;
      if (this.nativeControls) this.video.controls = true;
    } else if (autoplay) {
      this.video.muted = true;
    }
    this.video.play().catch(() => {});
    this.classList.add('is-playing');
    this.updateSoundIcon();
  }

  updateSoundIcon() {
    if (!this.soundBtn || !this.video) return;
    this.soundBtn.dataset.muted = this.video.muted ? 'true' : 'false';
  }
}

class DD0UGCCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.dd0-ugc-videos__viewport');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    if (!this.viewport) return;

    this.prevBtn?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByCard(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.scrollToIndex(parseInt(dot.dataset.dotIndex, 10)));
    });
    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });

    this.bindDrag();
    this.bindWheel();

    requestAnimationFrame(() => this.onScroll());
    window.addEventListener('resize', () => this.onScroll(), { passive: true });
  }

  bindDrag() {
    // Deferred pointer capture pattern: don't capture the pointer until movement
    // crosses the drag threshold. A pure click (no movement) flows normally to
    // children — preserving native click-to-pause on the <video>.
    const v = this.viewport;
    const THRESHOLD = 6;
    let armed = false;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let pointerId = null;

    const onDown = (e) => {
      if (e.target.closest('button, a, [data-dot-index]')) return;
      if (e.button !== undefined && e.button !== 0) return;
      armed = true;
      dragging = false;
      moved = 0;
      startX = e.clientX;
      startScroll = v.scrollLeft;
      pointerId = e.pointerId;
    };

    const onMove = (e) => {
      if (!armed) return;
      const dx = e.clientX - startX;
      moved = Math.abs(dx);
      if (!dragging && moved > THRESHOLD) {
        dragging = true;
        v.classList.add('is-dragging');
        try { v.setPointerCapture?.(pointerId); } catch (_) {}
      }
      if (dragging) {
        v.scrollLeft = startScroll - dx;
        if (e.cancelable) e.preventDefault();
      }
    };

    const onUp = () => {
      if (!armed) return;
      armed = false;
      if (dragging) {
        dragging = false;
        v.classList.remove('is-dragging');
        const blockNextClick = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        v.addEventListener('click', blockNextClick, { capture: true, once: true });
      }
    };

    v.addEventListener('pointerdown', onDown);
    v.addEventListener('pointermove', onMove);
    v.addEventListener('pointerup', onUp);
    v.addEventListener('pointercancel', onUp);
  }

  bindWheel() {
    const v = this.viewport;
    v.addEventListener('wheel', (e) => {
      // Only redirect when carousel can scroll horizontally AND the gesture is primarily vertical wheel
      const canScrollX = v.scrollWidth > v.clientWidth + 1;
      if (!canScrollX) return;
      // Trackpad horizontal gestures already produce deltaX — let them through natively
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      v.scrollBy({ left: e.deltaY, behavior: 'auto' });
    }, { passive: false });
  }

  cardStep() {
    const card = this.viewport.querySelector('.dd0-ugc-video');
    if (!card) return 0;
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap || getComputedStyle(this.viewport).gap || 0) || 0;
    return card.offsetWidth + gap;
  }

  scrollByCard(dir) {
    const step = this.cardStep();
    if (!step) return;
    this.viewport.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  scrollToIndex(idx) {
    const card = this.viewport.querySelectorAll('.dd0-ugc-video')[idx];
    if (!card) return;
    const pad = parseFloat(getComputedStyle(this.viewport).paddingInlineStart || 0) || 0;
    this.viewport.scrollTo({ left: card.offsetLeft - pad, behavior: 'smooth' });
  }

  onScroll() {
    this.updateArrows();
    this.updateDots();
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    const v = this.viewport;
    const atStart = v.scrollLeft <= 4;
    const atEnd = v.scrollLeft >= v.scrollWidth - v.clientWidth - 4;
    this.prevBtn.toggleAttribute('disabled', atStart);
    this.nextBtn.toggleAttribute('disabled', atEnd);
  }

  updateDots() {
    if (!this.dots.length) return;
    const step = this.cardStep();
    if (!step) return;
    const active = Math.round(this.viewport.scrollLeft / step);
    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('dd0-ugc-video')) {
  customElements.define('dd0-ugc-video', DD0UGCVideo);
}
if (!customElements.get('dd0-ugc-carousel')) {
  customElements.define('dd0-ugc-carousel', DD0UGCCarousel);
}

})();
